import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons/faArrowRight";
import { faArrowUp } from "@fortawesome/pro-regular-svg-icons/faArrowUp";
import { faArrowDown } from "@fortawesome/pro-regular-svg-icons/faArrowDown";
import { faTrash } from "@fortawesome/pro-regular-svg-icons/faTrash";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { useUpdateElementData } from "../DocumentView/DocumentContext";
import { cn } from "../lib/utils";

interface Props {
  element: DocumentElementType;
}

/**
 * Table data. `rows` is the full grid — row 0 / column 0 are styled as headers
 * when `headerRow` / `headerColumn` are set. (Legacy blocks stored the header
 * names in a separate `columns` array; those are migrated to row 0 on read.)
 */
interface TableData {
  rows?: string[][];
  columns?: string[]; // legacy
  columnWidths?: (number | null)[];
  headerRow?: boolean;
  headerColumn?: boolean;
}

const DEFAULT_GRID: string[][] = [
  ["Column 1", "Column 2", "Column 3"],
  ["", "", ""],
  ["", "", ""],
];
const MIN_COL_WIDTH = 60;
// New columns get an explicit, comfortable width (rather than auto-shrinking to
// their empty content). The add-column drag steps by the same amount so a drag
// of N px adds ~N px of columns and the strip tracks the cursor.
const NEW_COL_WIDTH = 140;
const ACCENT = "#2f6fdb";

const normaliseRow = (row: string[], n: number): string[] => {
  const out = row.slice(0, n);
  while (out.length < n) out.push("");
  return out;
};

/** How a cell is styled: a muted header-row label, a bold header-column, or a
 *  plain body cell. */
type CellVariant = "row" | "col" | undefined;

const cellTextClass = (variant: CellVariant) =>
  variant === "row"
    ? "text-[11.5px] font-bold tracking-[0.01em] text-[#5f5b54]"
    : variant === "col"
      ? "text-[13.5px] font-bold text-[#2a2723]"
      : "text-[13.5px] text-[#41403c]";

/** An editable grid cell (uncontrolled contentEditable, commits on blur). */
const Cell: React.FC<{
  value: string;
  editable: boolean;
  onCommit: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  variant?: CellVariant;
}> = ({ value, editable, onCommit, onFocus, onBlur, variant }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.textContent !== value) {
      el.textContent = value;
    }
  }, [value]);
  return (
    <div
      ref={ref}
      contentEditable={editable}
      suppressContentEditableWarning
      spellCheck={false}
      onFocus={onFocus}
      onBlur={(e) => {
        onCommit(e.currentTarget.textContent ?? "");
        onBlur();
      }}
      className={cn("min-w-0 outline-none", cellTextClass(variant))}
    />
  );
};

type Menu = { kind: "col" | "row"; index: number; top: number; left: number };

/**
 * The `page-table` block — a plain rows-and-columns grid. The controls live on
 * the borders: a slim handle centred on each column's top border and each row's
 * left border (thickening on hover) opens that column/row's menu, and a grabber
 * on each cell's right border resizes the column. The first row/column can be
 * toggled as a header (shaded). The active row/column and the focused cell get a
 * highlighted border. Read-only outside the editor.
 */
const PageTable: React.FC<Props> = ({ element }) => {
  const raw = (element.data ?? {}) as TableData;
  const update = useUpdateElementData(element.id);
  const editable = !!update;

  // Derive the grid (migrating the legacy {columns, rows} shape on read).
  const grid: string[][] = Array.isArray(raw.columns)
    ? [raw.columns, ...(Array.isArray(raw.rows) ? raw.rows : [])]
    : Array.isArray(raw.rows) && raw.rows.length
      ? raw.rows
      : DEFAULT_GRID;
  const colCount = Math.max(1, grid[0]?.length ?? 1);
  const rows = grid.map((r) => normaliseRow(r, colCount));
  const rowCount = rows.length;
  const useHeaderRow = raw.headerRow ?? Array.isArray(raw.columns);
  const useHeaderColumn = !!raw.headerColumn;
  const widths = raw.columnWidths ?? [];

  const [menu, setMenu] = useState<Menu | null>(null);
  const [focused, setFocused] = useState<string | null>(null); // "r:c"
  const [live, setLive] = useState<{ col: number; width: number } | null>(null);
  const cellRefs = useRef<Array<HTMLTableCellElement | null>>([]); // row-0 cells

  // The +/drag strips only appear while their own strip or the adjacent (last)
  // row/column is hovered. A brief leave-delay avoids flicker when the pointer
  // crosses between the last cell and the strip.
  const [hovCol, setHovCol] = useState(false);
  const [hovRow, setHovRow] = useState(false);
  const colHide = useRef<ReturnType<typeof setTimeout>>();
  const rowHide = useRef<ReturnType<typeof setTimeout>>();
  const colEnter = () => {
    clearTimeout(colHide.current);
    setHovCol(true);
  };
  const colLeave = () => {
    colHide.current = setTimeout(() => setHovCol(false), 60);
  };
  const rowEnter = () => {
    clearTimeout(rowHide.current);
    setHovRow(true);
  };
  const rowLeave = () => {
    rowHide.current = setTimeout(() => setHovRow(false), 60);
  };

  const withGrid = (fn: (d: Required<Omit<TableData, "columns">>) => void) =>
    update?.((rawDraft) => {
      const d = rawDraft as TableData;
      // Migrate legacy shape into the grid.
      if (Array.isArray(d.columns)) {
        d.rows = [d.columns, ...(Array.isArray(d.rows) ? d.rows : [])];
        if (typeof d.headerRow !== "boolean") d.headerRow = true;
        delete d.columns;
      }
      if (!Array.isArray(d.rows) || !d.rows.length)
        d.rows = DEFAULT_GRID.map((r) => [...r]);
      const n = Math.max(1, d.rows[0]?.length ?? 1);
      d.rows = d.rows.map((r) => normaliseRow(r, n));
      if (!Array.isArray(d.columnWidths)) d.columnWidths = [];
      // Keep the widths array aligned with the column count so add/remove-column
      // splices (and pushes) land on the right column.
      const cw = d.rows[0]?.length ?? 0;
      while (d.columnWidths.length < cw) d.columnWidths.push(null);
      d.columnWidths.length = cw;
      if (typeof d.headerRow !== "boolean") d.headerRow = false;
      if (typeof d.headerColumn !== "boolean") d.headerColumn = false;
      fn(d as Required<Omit<TableData, "columns">>);
    });

  const setCell = (r: number, c: number, v: string) =>
    withGrid((d) => {
      d.rows[r][c] = v;
    });
  const addRow = (at: number) =>
    withGrid((d) => {
      d.rows.splice(at, 0, new Array(d.rows[0].length).fill(""));
    });
  const removeRow = (at: number) =>
    withGrid((d) => {
      if (d.rows.length > 1) d.rows.splice(at, 1);
    });
  const addColumn = (at: number) =>
    withGrid((d) => {
      d.rows.forEach((r) => r.splice(at, 0, ""));
      d.columnWidths.splice(at, 0, NEW_COL_WIDTH);
    });
  const removeColumn = (at: number) =>
    withGrid((d) => {
      if ((d.rows[0]?.length ?? 0) <= 1) return;
      d.rows.forEach((r) => r.splice(at, 1));
      d.columnWidths.splice(at, 1);
    });
  const setColumnWidth = (c: number, w: number) =>
    withGrid((d) => {
      while (d.columnWidths.length < d.rows[0].length) d.columnWidths.push(null);
      d.columnWidths[c] = w;
    });
  const setHeaderRow = (v: boolean) =>
    withGrid((d) => {
      d.headerRow = v;
    });
  const setHeaderColumn = (v: boolean) =>
    withGrid((d) => {
      d.headerColumn = v;
    });

  // Quick append/remove at the far edges (the +/drag strips).
  const appendColumn = () =>
    withGrid((d) => {
      d.rows.forEach((r) => r.push(""));
      d.columnWidths.push(NEW_COL_WIDTH);
    });
  const popColumn = () =>
    withGrid((d) => {
      if ((d.rows[0]?.length ?? 0) > 1) {
        d.rows.forEach((r) => r.pop());
        d.columnWidths.pop();
      }
    });
  const appendRow = () =>
    withGrid((d) => {
      d.rows.push(new Array(d.rows[0].length).fill(""));
    });
  const popRow = () =>
    withGrid((d) => {
      if (d.rows.length > 1) d.rows.pop();
    });

  const edgeDrag =
    (axis: "x" | "y", step: number, add: () => void, remove: () => void) =>
    (e: React.PointerEvent) => {
      e.preventDefault();
      const el = e.currentTarget as HTMLElement;
      el.setPointerCapture(e.pointerId);
      const start = axis === "y" ? e.clientY : e.clientX;
      let acc = 0;
      let moved = false;
      const onMove = (ev: PointerEvent) => {
        const delta = (axis === "y" ? ev.clientY : ev.clientX) - start;
        if (Math.abs(delta) > 4) moved = true;
        while (delta - acc >= step) {
          add();
          acc += step;
        }
        while (delta - acc <= -step) {
          remove();
          acc -= step;
        }
      };
      const onUp = (ev: PointerEvent) => {
        if (!moved) add();
        el.releasePointerCapture(ev.pointerId);
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
      };
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", onUp);
    };

  const startResize = (c: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth =
      cellRefs.current[c]?.getBoundingClientRect().width ?? 160;
    const startX = e.clientX;
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    const compute = (ev: PointerEvent) =>
      Math.max(MIN_COL_WIDTH, Math.round(startWidth + (ev.clientX - startX)));
    const onMove = (ev: PointerEvent) => setLive({ col: c, width: compute(ev) });
    const onUp = (ev: PointerEvent) => {
      setColumnWidth(c, compute(ev));
      setLive(null);
      el.releasePointerCapture(ev.pointerId);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  const widthFor = (c: number): number | undefined => {
    if (live?.col === c) return live.width;
    const w = widths[c];
    return typeof w === "number" && w > 0 ? w : undefined;
  };

  const openMenu = (kind: "col" | "row", index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenu(
      menu?.kind === kind && menu.index === index
        ? null
        : {
            kind,
            index,
            top: r.bottom + 6,
            left: Math.max(8, Math.min(r.left - 4, window.innerWidth - 192)),
          },
    );
  };

  const activeRow = menu?.kind === "row" ? menu.index : null;
  const activeCol = menu?.kind === "col" ? menu.index : null;

  const cellIsHeader = (r: number, c: number) =>
    (useHeaderRow && r === 0) || (useHeaderColumn && c === 0);
  // Header row wins the corner (matches the design: the header row is a muted
  // label, the header column is bold body text).
  const cellVariant = (r: number, c: number): CellVariant =>
    useHeaderRow && r === 0
      ? "row"
      : useHeaderColumn && c === 0
        ? "col"
        : undefined;

  // Inset border highlight for the focused cell / active row / active column.
  const cellShadow = (r: number, c: number): string | undefined => {
    if (focused === `${r}:${c}`) return `inset 0 0 0 1.5px ${ACCENT}`;
    const top = `inset 0 1.5px 0 ${ACCENT}`;
    const bottom = `inset 0 -1.5px 0 ${ACCENT}`;
    const left = `inset 1.5px 0 0 ${ACCENT}`;
    const right = `inset -1.5px 0 0 ${ACCENT}`;
    const parts: string[] = [];
    // Highlight the whole active row/column as a closed box: the long edges run
    // down every cell, and the end cells add the top/bottom (or left/right) cap.
    if (activeRow === r) {
      parts.push(top, bottom);
      if (c === 0) parts.push(left);
      if (c === colCount - 1) parts.push(right);
    }
    if (activeCol === c) {
      parts.push(left, right);
      if (r === 0) parts.push(top);
      if (r === rowCount - 1) parts.push(bottom);
    }
    return parts.length ? parts.join(", ") : undefined;
  };

  // ---- read-only ----
  if (!editable) {
    return (
      <div className="max-w-full overflow-x-auto rounded-[9px] border border-[#e7e5e1] font-jakarta">
        <table className="border-separate border-spacing-0 text-[13.5px] text-[#41403c]">
          <colgroup>
            {rows[0].map((_, c) => (
              <col key={c} style={{ width: widthFor(c) }} />
            ))}
          </colgroup>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    className={cn(
                      "border-r border-[#f0efec] px-[13px] py-[9px] last:border-r-0",
                      r > 0 && "border-t",
                      r === 0 && c === 0 && "rounded-tl-[8px]",
                      r === 0 && c === colCount - 1 && "rounded-tr-[8px]",
                      r === rowCount - 1 && c === 0 && "rounded-bl-[8px]",
                      r === rowCount - 1 &&
                        c === colCount - 1 &&
                        "rounded-br-[8px]",
                      cellIsHeader(r, c) && "bg-[#f7f6f3]",
                      cellTextClass(cellVariant(r, c)),
                    )}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ---- editor ----
  return (
    <div className="inline-flex max-w-full flex-col font-jakarta">
      <div className="flex items-stretch">
        {/* The padding gives the border handles room to straddle the table edge
            without the scroll container clipping them; the matching negative
            margins cancel the visual offset so the table still sits flush. */}
        <div className="-ml-[9px] -mt-[9px] max-w-full overflow-x-auto pl-[9px] pt-[9px]">
          <div className="relative w-max rounded-[9px] border border-[#e7e5e1]">
            <table className="border-separate border-spacing-0">
              <colgroup>
                {rows[0].map((_, c) => (
                  <col key={c} style={{ width: widthFor(c) }} />
                ))}
              </colgroup>
              <tbody>
                {rows.map((row, r) => (
                  <tr key={r} className="group/row">
                    {row.map((cell, c) => {
                      const shadow = cellShadow(r, c);
                      return (
                        <td
                          key={c}
                          ref={
                            r === 0
                              ? (el) => (cellRefs.current[c] = el)
                              : undefined
                          }
                          onMouseEnter={() => {
                            if (c === colCount - 1) colEnter();
                            if (r === rowCount - 1) rowEnter();
                          }}
                          onMouseLeave={() => {
                            if (c === colCount - 1) colLeave();
                            if (r === rowCount - 1) rowLeave();
                          }}
                          style={shadow ? { boxShadow: shadow } : undefined}
                          className={cn(
                            "group/cell relative border-r border-[#f0efec] px-[13px] py-[9px] align-top last:border-r-0",
                            r > 0 && "border-t",
                            // Round the outer corners so the header shading and
                            // the selection highlight follow the table radius.
                            r === 0 && c === 0 && "rounded-tl-[8px]",
                            r === 0 && c === colCount - 1 && "rounded-tr-[8px]",
                            r === rowCount - 1 && c === 0 && "rounded-bl-[8px]",
                            r === rowCount - 1 &&
                              c === colCount - 1 &&
                              "rounded-br-[8px]",
                            cellIsHeader(r, c)
                              ? "bg-[#f7f6f3]"
                              : "hover:bg-[#faf9f7]",
                            shadow && "z-[1]",
                          )}
                        >
                          {/* Column handle — straddles the column's top border */}
                          {r === 0 && (
                            <button
                              type="button"
                              aria-label="Column options"
                              onClick={(e) => openMenu("col", c, e)}
                              className={cn(
                                "absolute left-1/2 top-0 z-20 h-[4px] w-[15px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#cfccc5] opacity-0 transition-all",
                                "hover:h-[7px] hover:w-[24px] hover:bg-[#a8a49c] group-hover/cell:opacity-100",
                                activeCol === c && "opacity-100",
                              )}
                            />
                          )}
                          {/* Row handle — straddles the row's left border */}
                          {c === 0 && (
                            <button
                              type="button"
                              aria-label="Row options"
                              onClick={(e) => openMenu("row", r, e)}
                              className={cn(
                                "absolute left-0 top-1/2 z-20 h-[15px] w-[4px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#cfccc5] opacity-0 transition-all",
                                "hover:h-[24px] hover:w-[7px] hover:bg-[#a8a49c] group-hover/row:opacity-100",
                                activeRow === r && "opacity-100",
                              )}
                            />
                          )}
                          {/* Resize grabber — the cell's right border */}
                          {r === 0 && (
                            <span
                              onPointerDown={(e) => startResize(c, e)}
                              className="group/rz absolute top-0 right-0 z-20 flex h-full w-[7px] cursor-col-resize touch-none items-stretch justify-center"
                            >
                              <span className="my-[3px] w-[2px] rounded-full bg-transparent transition-colors group-hover/rz:bg-[#2f6fdb]" />
                            </span>
                          )}
                          <Cell
                            editable
                            value={cell}
                            variant={cellVariant(r, c)}
                            onFocus={() => setFocused(`${r}:${c}`)}
                            onBlur={() =>
                              setFocused((f) => (f === `${r}:${c}` ? null : f))
                            }
                            onCommit={(v) => setCell(r, c, v)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add / drag columns (right edge). The 4px gap (pl) is part of the
            reveal hit-area, so hovering it counts too. */}
        <div
          onMouseEnter={colEnter}
          onMouseLeave={colLeave}
          className="flex flex-none pl-[4px]"
        >
          <div
            title="Click to add a column · drag to add or remove"
            onPointerDown={edgeDrag("x", NEW_COL_WIDTH, appendColumn, popColumn)}
            className={cn(
              "flex w-[15px] cursor-ew-resize touch-none select-none items-center justify-center rounded-[7px] bg-[#f5f4f1] text-[#c7c4bd] transition-opacity hover:bg-[#ecebe6] hover:text-[#8f8b83]",
              hovCol ? "opacity-100" : "opacity-0",
            )}
          >
            <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
          </div>
        </div>
      </div>

      {/* Add / drag rows (bottom edge). The 4px gap (pt) is part of the reveal
          hit-area. */}
      <div className="flex gap-[4px]">
        <div
          onMouseEnter={rowEnter}
          onMouseLeave={rowLeave}
          className="flex flex-1 pt-[4px]"
        >
          <div
            title="Click to add a row · drag to add or remove"
            onPointerDown={edgeDrag("y", 40, appendRow, popRow)}
            className={cn(
              "flex h-[15px] flex-1 cursor-ns-resize touch-none select-none items-center justify-center rounded-[7px] bg-[#f5f4f1] text-[#c7c4bd] transition-opacity hover:bg-[#ecebe6] hover:text-[#8f8b83]",
              hovRow ? "opacity-100" : "opacity-0",
            )}
          >
            <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
          </div>
        </div>
        <div className="w-[15px] flex-none" />
      </div>

      {menu &&
        createPortal(
          <div className="fixed inset-0 z-[9000] font-jakarta">
            <div className="absolute inset-0" onClick={() => setMenu(null)} />
            <div
              style={{ top: menu.top, left: menu.left }}
              className="absolute w-[184px] overflow-hidden rounded-[9px] border border-[#e7e5e1] bg-white py-[5px] shadow-[0_14px_36px_-12px_rgba(20,18,15,0.28)]"
            >
              {menu.kind === "col" ? (
                <>
                  {menu.index === 0 && (
                    <>
                      <ToggleItem
                        label="Header column"
                        on={useHeaderColumn}
                        onToggle={() => setHeaderColumn(!useHeaderColumn)}
                      />
                      <Divider />
                    </>
                  )}
                  <MenuItem
                    icon={faArrowLeft}
                    label="Insert left"
                    onClick={() => {
                      addColumn(menu.index);
                      setMenu(null);
                    }}
                  />
                  <MenuItem
                    icon={faArrowRight}
                    label="Insert right"
                    onClick={() => {
                      addColumn(menu.index + 1);
                      setMenu(null);
                    }}
                  />
                  <MenuItem
                    icon={faTrash}
                    label="Delete column"
                    danger
                    disabled={colCount <= 1}
                    onClick={() => {
                      removeColumn(menu.index);
                      setMenu(null);
                    }}
                  />
                </>
              ) : (
                <>
                  {menu.index === 0 && (
                    <>
                      <ToggleItem
                        label="Header row"
                        on={useHeaderRow}
                        onToggle={() => setHeaderRow(!useHeaderRow)}
                      />
                      <Divider />
                    </>
                  )}
                  <MenuItem
                    icon={faArrowUp}
                    label="Insert above"
                    onClick={() => {
                      addRow(menu.index);
                      setMenu(null);
                    }}
                  />
                  <MenuItem
                    icon={faArrowDown}
                    label="Insert below"
                    onClick={() => {
                      addRow(menu.index + 1);
                      setMenu(null);
                    }}
                  />
                  <MenuItem
                    icon={faTrash}
                    label="Delete row"
                    danger
                    disabled={rowCount <= 1}
                    onClick={() => {
                      removeRow(menu.index);
                      setMenu(null);
                    }}
                  />
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const Divider = () => <div className="my-[4px] h-px bg-[#f0efec]" />;

const MenuItem: React.FC<{
  icon: typeof faTrash;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}> = ({ icon, label, onClick, danger, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "flex w-full items-center gap-[10px] px-[12px] py-[7px] text-left text-[12.5px] font-semibold transition-colors",
      disabled
        ? "cursor-not-allowed text-[#c7c4bd]"
        : danger
          ? "text-[#c0303c] hover:bg-[#fdf0f1]"
          : "text-[#33302b] hover:bg-[#f6f7f9]",
    )}
  >
    <FontAwesomeIcon icon={icon} className="w-[13px] text-[12px]" />
    {label}
  </button>
);

const ToggleItem: React.FC<{
  label: string;
  on: boolean;
  onToggle: () => void;
}> = ({ label, on, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex w-full items-center justify-between px-[12px] py-[7px] text-left text-[12.5px] font-semibold text-[#33302b] transition-colors hover:bg-[#f6f7f9]"
  >
    {label}
    <span
      className={cn(
        "relative h-[16px] w-[28px] flex-none rounded-full transition-colors",
        on ? "bg-[#2f6fdb]" : "bg-[#d4dae1]",
      )}
    >
      <span
        className={cn(
          "absolute top-[2px] h-[12px] w-[12px] rounded-full bg-white transition-all",
          on ? "left-[14px]" : "left-[2px]",
        )}
      />
    </span>
  </button>
);

export default PageTable;
